/* =========================================================
	ANNUAL REPORT: TOTAL ASSETS BAR CHART
	Modern Chart.js v4 implementation with vanilla JavaScript
	Replaces jQuery + D3 + legacy Chart.js with modern approach
========================================================= */

class FinancialChart {
   constructor(canvasId, csvUrl) {
      this.canvas = document.getElementById(canvasId);
      this.csvUrl = csvUrl;
      this.chart = null;
      this.data = {
         labels: [],
         assets: [],
         approved: [],
         paid: [],
      };

      if (!this.canvas) {
         console.error(`Canvas element with id "${canvasId}" not found`);
         return;
      }

      if (!this.csvUrl) {
         console.error('CSV URL is required');
         return;
      }

      this.init();
   }

   async init() {
      try {
         await this.loadData();
         this.setupIntersectionObserver();
      } catch (error) {
         console.error('Failed to initialize chart:', error);
      }
   }

   /**
    * Set up Intersection Observer to trigger animation when chart comes into view
    */
   setupIntersectionObserver() {
      const observer = new IntersectionObserver(
         (entries) => {
            entries.forEach((entry) => {
               if (entry.isIntersecting) {
                  // Chart is in view, create it with animation
                  this.createChart();
                  observer.unobserve(entry.target); // Stop observing once animated
               }
            });
         },
         {
            threshold: 0.2, // Trigger when 20% of the chart is visible
            rootMargin: '0px 0px -50px 0px', // Trigger slightly before fully in view
         }
      );

      observer.observe(this.canvas);
   }

   /**
    * Load and parse CSV data using native fetch (replaces D3.csv)
    */
   async loadData() {
      try {
         const response = await fetch(this.csvUrl);
         if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
         }

         const csvText = await response.text();
         this.parseCSV(csvText);
      } catch (error) {
         console.error('Error loading CSV data:', error);
         throw error;
      }
   }

   /**
    * Simple CSV parser (replaces D3.csv dependency)
    */
   parseCSV(csvText) {
      const lines = csvText.split('\n');
      const headers = lines[0]
         .split(',')
         .map((header) => header.trim().replace(/"/g, ''));

      for (let i = 1; i < lines.length; i++) {
         const line = lines[i].trim();
         if (!line) continue;

         const values = this.parseCSVLine(line);
         if (values.length < headers.length) continue;

         const row = {};
         headers.forEach((header, index) => {
            row[header] = values[index]
               ? values[index].trim().replace(/"/g, '')
               : '';
         });

         // Parse the data (matching original logic)
         if (row['Year']) {
            this.data.labels.push(parseInt(row['Year'].replace(/,/g, ''), 10));
            this.data.assets.push(
               parseInt(row['Total Assets'].replace(/,/g, ''), 10)
            );
            this.data.approved.push(
               parseInt(row['Grants Approved'].replace(/,/g, ''), 10)
            );
            this.data.paid.push(
               parseInt(row['Grants Paid'].replace(/,/g, ''), 10)
            );
         }
      }
   }

   /**
    * Parse a single CSV line, handling commas within quoted values
    */
   parseCSVLine(line) {
      const result = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
         const char = line[i];

         if (char === '"') {
            inQuotes = !inQuotes;
         } else if (char === ',' && !inQuotes) {
            result.push(current);
            current = '';
         } else {
            current += char;
         }
      }

      result.push(current);
      return result;
   }

   /**
    * Create the Chart.js chart with modern v4 syntax
    */
   createChart() {
      const ctx = this.canvas.getContext('2d');

      this.chart = new Chart(ctx, {
         type: 'bar',
         data: {
            labels: this.data.labels,
            datasets: [
               {
                  label: 'Total Assets',
                  data: this.data.assets,
                  type: 'bar',
                  backgroundColor: 'rgba(200, 200, 200, 0.5)',
                  borderColor: 'rgba(200, 200, 200, 0.5)',
                  hoverBackgroundColor: 'rgba(200, 200, 200, 0.75)',
                  yAxisID: 'leftAxis',
                  order: 2,
               },
               {
                  label: 'Grants Approved',
                  data: this.data.approved,
                  type: 'line',
                  borderColor: 'rgba(80, 201, 181, 1)',
                  backgroundColor: 'rgba(80, 201, 181, 0.2)',
                  tension: 0.4,
                  fill: false,
                  yAxisID: 'rightAxis',
                  order: 1,
               },
               {
                  label: 'Grants Paid',
                  data: this.data.paid,
                  type: 'line',
                  borderColor: 'rgba(172, 146, 87, 1)',
                  backgroundColor: 'rgba(172, 146, 87, 1)',
                  tension: 0.4,
                  fill: false,
                  yAxisID: 'rightAxis',
                  order: 1,
               },
            ],
         },
         options: {
            responsive: true,
            maintainAspectRatio: false,

            // Modern Chart.js v4 animation configuration
            animation: {
               duration: 1200, // Slightly longer for dramatic effect
               easing: 'easeOutQuart',
               delay: (context) => {
                  // Stagger animations: bars first, then lines
                  if (context.type === 'data' && context.mode === 'default') {
                     const datasetIndex = context.datasetIndex;
                     const dataIndex = context.dataIndex;

                     // Bars (dataset 0) animate first
                     if (datasetIndex === 0) {
                        return dataIndex * 30; // Stagger bars by 30ms each
                     }
                     // Lines (datasets 1 & 2) animate after bars
                     else {
                        return 400 + (datasetIndex - 1) * 200; // Lines start after 400ms
                     }
                  }
                  return 0;
               },
            },

            // Interaction and hover settings
            interaction: {
               intersect: false,
               mode: 'index',
            },

            // Plugin configurations
            plugins: {
               legend: {
                  labels: {
                     color: '#5e5e5f',
                     font: {
                        size: 16,
                        family: 'calluna_sans, Helvetica, Arial, sans-serif',
                     },
                     filter: function (legendItem) {
                        // Clean up legend labels (remove axis indicators)
                        legendItem.text = legendItem.text
                           .replace(' (right axis)', '')
                           .replace(' (left axis)', '');
                        return true;
                     },
                  },
               },
               tooltip: {
                  backgroundColor: 'rgba(60, 60, 60, 0.8)',
                  padding: {
                     x: 9,
                     y: 9,
                  },
                  caretSize: 10,
                  callbacks: {
                     label: (context) => {
                        let label = context.dataset.label;
                        let value = context.parsed.y.toString();

                        // Add comma formatting
                        value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

                        return `${label}: $${value}`;
                     },
                  },
               },
            },

            // Modern Chart.js v4 scales configuration
            scales: {
               x: {
                  display: true,
                  grid: {
                     display: false,
                  },
                  ticks: {
                     maxTicksLimit: 11,
                     color: '#7b7b7c',
                  },
               },
               leftAxis: {
                  type: 'linear',
                  position: 'left',
                  grid: {
                     display: true,
                     color: '#dcf4f0',
                  },
                  ticks: {
                     color: '#7b7b7c',
                     callback: function (value) {
                        // Format numbers with commas
                        const formatted = value
                           .toString()
                           .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        return `$${formatted}`;
                     },
                  },
               },
               rightAxis: {
                  type: 'linear',
                  position: 'right',
                  grid: {
                     display: true,
                     color: '#e3e3e3',
                  },
                  ticks: {
                     color: '#4fc9b5',
                     callback: function (value) {
                        // Format numbers with commas
                        const formatted = value
                           .toString()
                           .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        return `$${formatted}`;
                     },
                  },
               },
            },
         },
      });
   }

   /**
    * Destroy the chart instance (useful for cleanup)
    */
   destroy() {
      if (this.chart) {
         this.chart.destroy();
         this.chart = null;
      }
   }
}

/**
 * Initialize charts when DOM is ready (replaces jQuery document.ready)
 */
function initializeFinancialCharts() {
   const chartCanvas = document.getElementById('stacked-financial-chart');

   if (chartCanvas) {
      const csvUrl = chartCanvas.dataset.csvUrl;

      if (csvUrl) {
         new FinancialChart('stacked-financial-chart', csvUrl);
      } else {
         console.error('CSV URL not found in data-csv-url attribute');
      }
   }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
   document.addEventListener('DOMContentLoaded', initializeFinancialCharts);
} else {
   // DOM is already ready
   initializeFinancialCharts();
}

// Export for module usage
export { FinancialChart, initializeFinancialCharts };
